/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { useUpsertMedia } from '@api/hooks';
import * as mediaUtils from '@utils/media';
import { MediaLibraryModal } from '@molecules';

import Form from '../Form';
import MediaFileSelect from '../MediaFileSelect';
import MediaDetailsForm from '../MediaDetailsForm';
import { FORM_FIELDS } from '../MediaDetailsForm/constants';

const STEP = {
  SELECT_FILE: 0,
  EDIT_DETAILS: 1,
};

const getOkText = (step, file, media) => {
  if (step === STEP.SELECT_FILE) return 'NEXT';

  const type = mediaUtils.getType(file, media);
  const typeName = mediaUtils.getTypeName(type).toUpperCase();

  if (media) return `UPDATE ${typeName}`;
  return `ADD ${typeName}`;
};

const UpsertMediaModal = ({ visible, hide, media, type }) => {
  const initialStep = media ? STEP.EDIT_DETAILS : STEP.SELECT_FILE;
  const [step, setStep] = React.useState(initialStep);
  const [file, setFile] = React.useState(null);
  const [form] = Form.useForm(FORM_FIELDS);

  const [upsertMedia, upsertMediaQuery] = useUpsertMedia({ throwOnError: true });

  const handleClose = React.useCallback(() => {
    if (step === STEP.SELECT_FILE && media) {
      setStep(STEP.EDIT_DETAILS);
      return;
    }

    if (file?.imageUrl) URL.revokeObjectURL(file.imageUrl);
    setStep(initialStep);
    setFile(null);
    form.resetFields();
    hide();
  }, [file, form, hide, initialStep, media, step]);

  const handleOnOk = React.useCallback(async () => {
    if (step === STEP.SELECT_FILE) setStep(STEP.EDIT_DETAILS);
    if (step === STEP.EDIT_DETAILS) {
      try {
        const values = await form.remoteSubmit();

        await upsertMedia({
          id: media?.id,
          fileName: media?.fileName,
          type: media?.type || mediaUtils.getType(file),
          file,
          ...values,
        });

        handleClose();
      } catch (e) {
        console.log(e);
      }
    }
  }, [upsertMedia, file, form, handleClose, media, step]);

  const handleReupload = React.useCallback(() => {
    setStep(STEP.SELECT_FILE);
  }, []);

  const okText = getOkText(step, file, media);
  const okDisabled = step === STEP.SELECT_FILE && !file;
  const isLoading = upsertMediaQuery.status === 'loading';

  return (
    <MediaLibraryModal
      visible={visible}
      onCancel={handleClose}
      okText={okText}
      loading={isLoading}
      onOk={handleOnOk}
      okDisabled={okDisabled}
      error={upsertMediaQuery.error}
    >
      {step === STEP.SELECT_FILE && <MediaFileSelect type={type} file={file} media={media} onChange={setFile} />}
      {step === STEP.EDIT_DETAILS && (
        <MediaDetailsForm file={file} media={media} form={form} onReupload={handleReupload} />
      )}
    </MediaLibraryModal>
  );
};

export default UpsertMediaModal;
